// libraries.
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CountryCode, ICountrySelect } from '@domains/country';
import { RootState } from '@store';
import { RolesPolynatorEnum } from '../../api-slices/roles-polynator.endpoint';
import { DocRolesEnum } from '../../api-slices/roles-documents.endpoints';

export interface StateUserProfile {
  countriesOptions?: ICountrySelect[];
  country?: CountryCode;
  email: string;
  familyName?: string;
  givenName?: string;
  googleId?: string;
  imageUrl?: string;
  name?: string;
  /** Roles de Cognito: independientes del país, persisten al cambiar de país. */
  cognitoRoles?: string[];
  /** Roles del API: específicos por país, se limpian al cambiar de país. */
  roles?: string[];
}

export const initialState: StateUserProfile = {
  countriesOptions: [],
  country: undefined,
  email: '',
  familyName: '',
  givenName: '',
  googleId: '',
  imageUrl: '',
  name: '',
  cognitoRoles: [],
  roles: [],
};

export const domainUserAuthSlice = createSlice({
  name: 'domainUserAuth',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<StateUserProfile>) => {
      return { ...state, ...action.payload };
    },
    setCountriesOptions: (state, action: PayloadAction<ICountrySelect[]>) => {
      state.countriesOptions = action.payload;
    },
    setCountry: (state, action: PayloadAction<CountryCode | undefined>) => {
      state.country = action.payload;
    },
    setEmail: (state, action: PayloadAction<string>) => {
      state.email = action.payload;
    },
    setFamilyName: (state, action: PayloadAction<string>) => {
      state.familyName = action.payload;
    },
    setGivenName: (state, action: PayloadAction<string>) => {
      state.givenName = action.payload;
    },
    setGoogleId: (state, action: PayloadAction<string>) => {
      state.googleId = action.payload;
    },
    setImageUrl: (state, action: PayloadAction<string>) => {
      state.imageUrl = action.payload;
    },
    setName: (state, action: PayloadAction<string>) => {
      state.name = action.payload;
    },
    setRoles: (state, action: PayloadAction<string>) => {
      if (!state.roles) {
        state.roles = [];
      }
      if (!state.roles.includes(action.payload)) {
        state.roles.push(action.payload);
      }
    },
    setRolesFromCognito: (state, action: PayloadAction<string[]>) => {
      state.cognitoRoles = [...new Set(action.payload)];
    },
    clearRoles: (state) => {
      state.roles = [];
    },
    setDefaultState: () => {
      return initialState;
    },
  },
});

export const {
  setUser,
  setCountriesOptions,
  setCountry,
  setEmail,
  setFamilyName,
  setGivenName,
  setGoogleId,
  setImageUrl,
  setName,
  setRoles,
  setRolesFromCognito,
  clearRoles,
  setDefaultState,
} = domainUserAuthSlice.actions;

export default domainUserAuthSlice.reducer;

// selectors.
export const getEmail = (state: RootState) => state.domainUserAuth.email;

export const getUserName = (state: RootState) => state.domainUserAuth.name;

export const getGivenName = (state: RootState) => state.domainUserAuth.givenName;

export const getFamilyName = (state: RootState) => state.domainUserAuth.familyName;

export const getCountry = (state: RootState) => state.domainUserAuth.country;

export const getRole = (state: RootState) => [
  ...(state.domainUserAuth.cognitoRoles ?? []),
  ...(state.domainUserAuth.roles ?? []),
];

export const getIsRoleIsPolygonCreator = (state: RootState) =>
  state.domainUserAuth.roles?.includes(RolesPolynatorEnum.PolygonCreator) ??
  false;

export const getIsDocumentReviewerRole = (state: RootState) =>
  state.domainUserAuth.roles?.includes(DocRolesEnum.DocumentReviewer) ?? false;
